import { ReactNode, useRef } from 'react';
import { Box, Dialog, useTheme } from '@mui/material';
import {
  StyledChartWrapper,
  boxIcon,
  iconsWrapper,
  StyledBoxPanIcon,
  StyledChartHeader,
} from './style';
import { TimeFrameBarOptions } from 'src/features/trends/components/timeframe-bar-options';

import { MenuPopoverOptions } from 'src/features/trends/components/menu-popover-options';
import { GraphSeriesType } from 'src/features/trends/types/readings';
import { AlarmData } from 'src/features/alarms';
import { exportAsPNG } from 'src/utilities';
import { TimeOptionType } from 'src/features/trends/types';
import { TimeframePopover } from 'src/features/trends/components/timeframe-popover/TimeframePopover';
import { SelectedBoxType } from 'src/features/trends/components/timeframe-bar-options/types';
import { SessionStorageService } from 'src/features/authentication/utils';
import AddChartModalContent from 'src/features/trends/components/add-chart-modal-content/AddChartModalContent';
import { SelectedInputType } from 'src/features/trends/types/selected-input';
import { getTimestampsBasedOnTimeBox } from 'src/features/trends/components/line-chart-trend/utils';
import { DateRangeComponent } from '../date-range';
import { SiteTrendsChartModal } from 'src/features';
import ArrowForwardIos from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosNew from '@mui/icons-material/ArrowBackIosNew';
import dayjs from 'dayjs';
import { useToggle } from 'src/hooks/useToggle';
import { useHtmlElement } from 'src/hooks/useHtmlElement';
import { useResponsive } from 'src/hooks/useResponsive';
import { GetAllResponse } from 'src/services/sites/types';
import { AxisXIcon } from 'src/assets/icons/charts/AxisXIcon';
import { AxisXyIcon } from 'src/assets/icons/charts/AxisXyIcon';
import { DisabledPanIcon } from 'src/assets/icons/DisabledPanIcon';
import { HamburgerIcon } from 'src/assets/icons/HamburgerIcon';
import { HomeIcon } from 'src/assets/icons/HomeIcon';
import { PanIcon } from 'src/assets/icons/PanIcon';
import { ZoomInIcon } from 'src/assets/icons/ZoomInIcon';
import { ZoomOutIcon } from 'src/assets/icons/ZoomOutIcon';

type Props = {
  children: ReactNode;
  zoomOutHandler: () => void;
  zoomInHandler: () => void;
  onResetZoomHandler: () => void;
  onResetTimestamp: () => void;
  isPanAndZoomActive: boolean;
  togglePanAndZoom: () => void;
  pointChartData?: GraphSeriesType[];
  alarms?: AlarmData[];
  removeChart?: (chartId: string) => void;
  id: string;
  timestamps: any;
  onChangeTimestapHandler: (values: [Date, Date]) => void;
  onSelectTimestampHandler: (days: number) => void;
  selectTabletTimeOptionHandler: (timeOption: TimeOptionType) => void;
  setTimestamps: (timestamps: any) => void;
  sessionStorageKey: string;
  selectedBoxSessionStorageKey: string;
  sites?: { value: GetAllResponse[]; loaded: boolean };
  onSelectEventHandler?: (selectedInputs: SelectedInputType[], id: string) => void;
  selectedBox: TimeOptionType;
  setSelectedBox: (value: TimeOptionType) => void;
  isSyncTimeframe: boolean;
  toggleSyncTimeframe: () => void;
  changeTimeFormatHandler: (fromDate: Date, toDate: Date) => void;
  toggleModeZoomOption?: (mode: 'x' | 'xy') => void;
  activeZoomMode: 'x' | 'xy';
  isLocalTrends?: boolean;
};

export const ChartWrapper = ({
  children,
  zoomInHandler,
  zoomOutHandler,
  onResetZoomHandler,
  onResetTimestamp,
  isPanAndZoomActive,
  togglePanAndZoom,
  alarms,
  pointChartData,
  removeChart,
  id,
  timestamps,
  onChangeTimestapHandler,
  onSelectTimestampHandler,
  selectTabletTimeOptionHandler,
  setTimestamps,
  sessionStorageKey,
  selectedBoxSessionStorageKey,
  sites,
  onSelectEventHandler,
  selectedBox,
  setSelectedBox,
  isSyncTimeframe,
  toggleSyncTimeframe,
  changeTimeFormatHandler,
  toggleModeZoomOption,
  activeZoomMode,
  isLocalTrends,
}: Props) => {
  const { htmlElement, addHtmlElement, removeHtmlElement } = useHtmlElement();
  const theme = useTheme();
  const iconColor = theme.palette.grey[500];
  const chartRef = useRef(null);
  const { state, onToggle } = useToggle();

  const onSelectBoxHandler = (value: SelectedBoxType) => {
    setSelectedBox(value);
    SessionStorageService.set(selectedBoxSessionStorageKey, value);

    const timestampsBasedOnSelectedTimeBox = getTimestampsBasedOnTimeBox(value);
    const fromDate = timestampsBasedOnSelectedTimeBox?.fromDate;
    const toDate = timestampsBasedOnSelectedTimeBox?.toDate;

    if (value !== 'custom') changeTimeFormatHandler(fromDate.toDate(), toDate.toDate());
  };

  const isTablet = useResponsive('down', 'lg');

  const onRemoveHandler = () => {
    if (removeChart && id) removeChart(id);
    onClosePopover();
  };

  const onOpenPopover = (event: any) => {
    addHtmlElement(event.currentTarget);
  };

  const onClosePopover = () => {
    removeHtmlElement();
  };

  const handleDownloadPNGClick = () => {
    onClosePopover();
    exportAsPNG(chartRef);
  };

  const onSelectDataHandler = () => {
    onToggle();
    onClosePopover();
  };

  const onResetHandler = () => {
    onResetZoomHandler();
    onResetTimestamp();
  };

  const onChangeActiveMode = (mode: 'x' | 'xy') => {
    if (toggleModeZoomOption) toggleModeZoomOption(mode);
  };

  const to = dayjs(timestamps.toDate);
  const forwardDisabled = !to.isBefore(dayjs(), 'day');

  const shiftTimeframe = (direction: 'backward' | 'forward') => {
    const from = dayjs(timestamps.fromDate);
    const to = dayjs(timestamps.toDate);
    const now = dayjs();

    const fromMs = from.valueOf();
    const toMs = to.valueOf();
    const diffMs = toMs - fromMs;
    if (diffMs <= 0) return;

    if (direction === 'backward') {
      const newFrom = dayjs(fromMs - diffMs);
      const newTo = dayjs(toMs - diffMs);

      setTimestamps({ fromDate: newFrom, toDate: newTo });
      onSelectBoxHandler('custom');
      changeTimeFormatHandler(newFrom.toDate(), newTo.toDate());
      SessionStorageService.set(
        sessionStorageKey,
        JSON.stringify({ fromDate: newFrom.toISOString(), toDate: newTo.toISOString() })
      );
      return;
    }

    const candidateTo = dayjs(toMs + diffMs);
    let newTo = candidateTo;
    let newFrom = dayjs(fromMs + diffMs);

    if (candidateTo.isAfter(now, 'day')) {
      newTo = now;
      newFrom = now.subtract(diffMs, 'millisecond');
    }

    setTimestamps({ fromDate: newFrom, toDate: newTo });
    onSelectBoxHandler('custom');
    changeTimeFormatHandler(newFrom.toDate(), newTo.toDate());
    SessionStorageService.set(
      sessionStorageKey,
      JSON.stringify({ fromDate: newFrom.toISOString(), toDate: newTo.toISOString() })
    );
  };

  return (
    <StyledChartWrapper theme={theme} ref={chartRef} isTablet={isTablet}>
      {isLocalTrends ? (
        <SiteTrendsChartModal
          state={state}
          onToggle={onToggle}
          chartId={id}
          isExistingChart={true}
        />
      ) : (
        <Dialog open={state} onClose={onToggle} maxWidth="lg">
          <AddChartModalContent
            onSelectEventHandler={onSelectEventHandler}
            onToggleHandler={onToggle}
            isParticularChart
            chartId={id}
            sites={sites}
          />
        </Dialog>
      )}
      <StyledChartHeader isTablet={isTablet} isSyncTimeframe={isSyncTimeframe}>
        {!isSyncTimeframe && isTablet ? (
          <TimeframePopover
            timestamps={timestamps}
            onSelectTimestampHandler={onSelectTimestampHandler}
            selectTabletTimeOptionHandler={selectTabletTimeOptionHandler}
            setTimestamps={setTimestamps}
            sessionStorageKey={sessionStorageKey}
            onResetZoomHandler={onResetZoomHandler}
            selectedBox={selectedBox}
            changeTimeFormatHandler={changeTimeFormatHandler}
          />
        ) : null}

        {!isSyncTimeframe && !isTablet ? (
          <Box sx={{ marginRight: '9rem', display: 'flex', alignItems: 'center' }}>
            <TimeFrameBarOptions
              onSelectTimestampHandler={onSelectTimestampHandler}
              selectedBox={selectedBox}
              onSelectBoxHandler={onSelectBoxHandler}
              onResetZoomHandler={onResetZoomHandler}
            />
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={boxIcon} onClick={() => shiftTimeframe('backward')}>
                <ArrowBackIosNew fontSize="small" />
              </Box>

              <DateRangeComponent
                onSelectBoxHandler={onSelectBoxHandler}
                onChangeTimestapHandler={onChangeTimestapHandler}
                timestamps={timestamps}
                changeTimeFormatHandler={changeTimeFormatHandler}
              />

              <Box
                sx={{
                  ...boxIcon,
                  opacity: forwardDisabled ? 0.3 : 1,
                  pointerEvents: forwardDisabled ? 'none' : 'auto',
                }}
                onClick={() => shiftTimeframe('forward')}
              >
                <ArrowForwardIos fontSize="small" />
              </Box>
            </Box>
          </Box>
        ) : null}

        <Box sx={iconsWrapper}>
          {activeZoomMode === 'x' ? (
            <Box sx={boxIcon} onClick={() => onChangeActiveMode('xy')}>
              <AxisXIcon />
            </Box>
          ) : (
            <Box sx={boxIcon} onClick={() => onChangeActiveMode('x')}>
              <AxisXyIcon />
            </Box>
          )}

          <Box sx={boxIcon} onClick={zoomInHandler}>
            <ZoomInIcon />
          </Box>
          <Box sx={boxIcon} onClick={zoomOutHandler}>
            <ZoomOutIcon />
          </Box>
          <Box sx={boxIcon} onClick={onResetHandler}>
            <HomeIcon fill={iconColor} />
          </Box>
          <StyledBoxPanIcon
            onClick={togglePanAndZoom}
            isPanAndZoomActive={isPanAndZoomActive}
            theme={theme}
          >
            {isPanAndZoomActive ? <PanIcon /> : <DisabledPanIcon />}
          </StyledBoxPanIcon>
          <Box onClick={onOpenPopover} data-sm="chart-burger-menu" sx={boxIcon}>
            <HamburgerIcon fill={iconColor} />
          </Box>
          <MenuPopoverOptions
            htmlElement={htmlElement}
            onClosePopover={onClosePopover}
            pointChartData={pointChartData}
            alarms={alarms}
            handleDownloadPNGClick={handleDownloadPNGClick}
            onRemoveHandler={onRemoveHandler}
            onSelectDataHandler={onSelectDataHandler}
            isSyncTimeframe={isSyncTimeframe}
            toggleSyncTimeframe={toggleSyncTimeframe}
          />
        </Box>
      </StyledChartHeader>
      {children}
    </StyledChartWrapper>
  );
};
